import { nflTeamAbbrFromLabel } from "./nflFranchises";

export const US_MAJOR_SCOPE = "us-major";
export const US_MAJOR_CFL_SCOPE = "us-major-cfl";
export const ALLOWED_LEAGUE_SCOPES = [US_MAJOR_SCOPE, US_MAJOR_CFL_SCOPE];

const US_MAJOR_TOKENS = new Set(["NFL", "AFL", "AAFC"]);
const CFL_TOKENS = new Set(["CFL", "IRFU", "WIFU"]);
const OTHER_TOKENS = new Set(["UFL", "XFL", "USFL", "WFL"]);
const KNOWN = new Set([...US_MAJOR_TOKENS, ...CFL_TOKENS, ...OTHER_TOKENS]);

function throwBadRequest(message) {
  const err = new Error(message);
  err.status = 400;
  throw err;
}

export function normalizeLeagueScope(value) {
  if (value == null || value === "") return US_MAJOR_SCOPE;
  if (ALLOWED_LEAGUE_SCOPES.includes(value)) return value;
  throwBadRequest(`Unknown leagueScope "${value}".`);
}

function leagueTokens(label) {
  const text = String(label || "");
  const tokens = [];
  for (const match of text.matchAll(/\(([A-Za-z]{2,5})\)/g)) {
    tokens.push(match[1].toUpperCase());
  }
  for (const match of text.matchAll(/\b[A-Z]{2,4}\s+([A-Z]{2,5})\b/g)) {
    const token = match[1].toUpperCase();
    if (KNOWN.has(token)) tokens.push(token);
  }
  return tokens;
}

function looksLikeNflFranchise(team, label) {
  return Boolean(nflTeamAbbrFromLabel(team) || nflTeamAbbrFromLabel(label));
}

export function classifySeason(label, team) {
  const tokens = leagueTokens(label);
  if (tokens.some((t) => CFL_TOKENS.has(t))) return "cfl";
  if (tokens.some((t) => US_MAJOR_TOKENS.has(t))) return "us-major";
  if (tokens.some((t) => OTHER_TOKENS.has(t)) || tokens.length) return "other";
  return looksLikeNflFranchise(team, label) ? "us-major" : "other";
}

export function seasonInScope(label, team, leagueScope) {
  const scope = normalizeLeagueScope(leagueScope);
  const cls = classifySeason(label, team);
  if (cls === "us-major") return true;
  if (cls === "cfl") return scope === US_MAJOR_CFL_SCOPE;
  return false;
}

export function parseStatNumber(raw) {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === "—" || trimmed === "-") return null;
  const num = Number(trimmed.replace(/,/g, ""));
  return Number.isFinite(num) ? num : null;
}

function clampRatingPart(value) {
  if (value < 0) return 0;
  if (value > 2.375) return 2.375;
  return value;
}

export function nflPasserRating({ att, com, yds, td, int: intercepts } = {}) {
  if (!att || att <= 0) return null;
  const a = clampRatingPart((com / att - 0.3) * 5);
  const b = clampRatingPart((yds / att - 3) * 0.25);
  const c = clampRatingPart((td / att) * 20);
  const d = clampRatingPart(2.375 - (intercepts / att) * 25);
  return ((a + b + c + d) / 6) * 100;
}

export function sumGroupSeasons(seasonsMap, leagueScope, countingFields) {
  const counts = {};
  countingFields.forEach((field) => {
    counts[field] = 0;
  });
  let seasonCount = 0;
  let firstYear = null;
  let lastYear = null;

  Object.entries(seasonsMap || {}).forEach(([yearKey, row]) => {
    if (!seasonInScope(row?.season, row?.team, leagueScope)) return;
    seasonCount += 1;
    const year = Number(yearKey);
    if (Number.isFinite(year)) {
      if (firstYear == null || year < firstYear) firstYear = year;
      if (lastYear == null || year > lastYear) lastYear = year;
    }
    countingFields.forEach((field) => {
      const value = parseStatNumber(row?.[field]);
      if (value != null) counts[field] += value;
    });
  });

  return { counts, seasonCount, firstYear, lastYear };
}
